/**
 * Serbian overrides for the fiveS screens.
 *
 * Bosnian moved from ijekavica to ekavica — *uspjela* to *uspela*, *mjesec*
 * to *mesec*, *prijedlog* to *predlog*. Where the two standards agree there
 * is no entry, which is why this file is short and should stay that way.
 *
 * Latin script, matching the rest of the application.
 *
 * This screen is dense with *ocjena* and its family, so most of what follows
 * is the same jat reflex applied over and over: *ocjena* → *ocena*,
 * *ocijenjen* → *ocenjen*, *ocjenjivanje* → *ocenjivanje*.
 */

import type { FiveSSlice } from '../bs/fiveS'

export const fiveS: Partial<FiveSSlice> = {
  'fiveS.denied.description': '5S ocenu unose nadležni, komisija i administrator.',

  'fiveS.help.oneWalk':
    'Jedan obilazak po mašini, po danu, po smeni. Jutarnji i noćni obilazak iste mašine istog dana su dva odvojena zapisa, jer opisuju dva različita stanja centra.',

  'fiveS.date.help':
    'Upisujete dan kad ste stvarno obišli mašinu, a ne dan kad unosite ocene — zapis opisuje stanje centra tog dana.',

  // *smjena* → *smena*.
  'fiveS.shift': 'Smena',
  'fiveS.list.allShifts': 'Sve smene',
  'fiveS.shift.hint': 'Koja smena je držala centar. Nije obavezno.',
  'fiveS.shift.help':
    'Aplikacija predlaže smenu koja traje u ovom trenutku; ako pogon ne radi u smenama, ovog polja nema.',
  'fiveS.shift.none': 'Bez smene',

  'fiveS.workers.help':
    'Iz ovog spiska se računa 5S ocena po radniku, a ona opisuje stanje mašine koju su držali, ne njih same.',

  'fiveS.onlyUnanswered': 'Prikaži samo neocenjene ({count})',
  'fiveS.onlyUnanswered.hint':
    'Korisno pri kraju obilaska, da ne tražite propušteno po celoj listi.',

  'fiveS.comment.hint': 'Vidljiva rukovodstvu uz ocenu.',
  'fiveS.comment.placeholder':
    'npr. Alat vraćen na mesto, ali obeležavanje zona nije završeno.',

  'fiveS.progressLabel': 'Ocenjeni kriterijumi',

  'fiveS.submit.hint':
    'Predaja je moguća tek kada su svi kriterijumi ocenjeni. Nacrt možete sačuvati bilo kada.',
  'fiveS.submit.help':
    'Predata evaluacija se više ne menja jer je to zapis stanja mašine tog dana; ponovo je otvara samo administrator.',
  // *sljedeći* → *sledeći*.
  'fiveS.submitted.next': 'Evaluacija je predata — {score}. Izaberite sledeći radni centar.',
  'fiveS.submit.blocked': 'Pre predaje ocenite sve kriterijume. Neocenjeno: {count}.',
  'fiveS.saveFailed': 'Čuvanje nije uspelo.',

  // *promijeniti* → *promeniti*, *ovdje* → *ovde*, *prenijeti* → *preneti*.
  'fiveS.centerChange.title': 'Promeniti radni centar?',
  'fiveS.centerChange.stay': 'Ostani ovde',
  'fiveS.centerChange.confirm': 'Promeni i obriši ocene',
  'fiveS.centerChange.body':
    'Svaki radni centar ima svoj obrazac, pa se unesene ocene ne mogu preneti. Broj unesenih ocena: {count}. Promenom radnog centra sve se brišu.',

  // *naljepnica* → *nalepnica*, and with it *zalijepiti* → *zalepiti*.
  'fiveS.stickers.title': 'QR nalepnice',
  'fiveS.stickers.subtitle':
    'Nalepnice za mašine. Radnik skenira kod i prijava se otvara sa već izabranim radnim centrom.',
  'fiveS.stickers.printHint':
    'Odštampajte na A4, isecite po isprekidanoj liniji i plastificirajte. Zalepite u visini očiju, dalje od prašine i ulja.',
  'fiveS.stickers.regenerateConfirm':
    'Obnoviti QR kod za „{name}"?\n\nNalepnica koja je trenutno na mašini prestaje da važi odmah. Moraćete odštampati i zalepiti novu.',
  'fiveS.stickers.regenerateFailed': 'Obnavljanje nije uspelo.',
  'fiveS.list.readOnly': '5S evaluacije obavljaju odgovorna lica — ovde ih možete pregledati.',
  'fiveS.detail.edit': 'Izmeni',

  // Ekavica for the strings the Bosnian copy carries a jat into.
  'fiveS.insights.history.hint': 'Poslednjih dvanaest obilazaka, najstariji prvi.',
  'fiveS.insights.history.tooShort': 'Treba još jedan obilazak da bi se video smer.',
  'fiveS.insights.criteria.hint': 'Kriterijumi ocenjeni ispod {target} skale, najslabiji prvi.',
  'fiveS.insights.empty': 'Nema ocena na ovom obilasku.',
  'fiveS.insights.series.score': 'Ocena',
  'fiveS.insights.rank.title': 'Mesto u pogonu ovog meseca',
  'fiveS.insights.rank.plantAverage': 'Prosek pogona',


  'fiveS.insights.rank.title.several': 'Mesto u vašim pogonima ovog meseca',
  'fiveS.insights.rank.title.all': 'Mesto u firmi ovog meseca',
  'fiveS.insights.rank.plantAverage.several': 'Prosek vaših pogona',
  'fiveS.insights.rank.plantAverage.all': 'Prosek firme',

  // ---- The 5S board and the walk -----------------------------------
  //
  // *mjesec* → *mesec* above all, then *ocijeniti* → *oceniti*,
  // *ovdje* → *ovde* and *poslednji* for *zadnji*.
  'fiveS.page.subtitle': 'Koje mašine još treba obići ovaj mesec i kako su prošle obiđene.',
  'fiveS.tabs.month': 'Ovaj mesec',
  'fiveS.board.month.previous': 'Prethodni mesec',
  'fiveS.board.month.next': 'Sledeći mesec',
  'fiveS.board.month.current': 'Na ovaj mesec',
  'fiveS.board.average': 'Prosek: {value}',
  'fiveS.board.withoutTemplate.help':
    'Mašina bez 5S obrasca ne može se oceniti, pa se ne broji. Obrazac dodaje administrator.',
  'fiveS.board.past': 'Ovo je raniji mesec. Novi obilazak se uvek upisuje u tekući mesec.',
  'fiveS.board.col.score': 'Ocena',
  'fiveS.board.col.trend': 'Poslednjih 6 meseci',
  'fiveS.board.lastSeen': 'Poslednja:',
  'fiveS.board.never': 'Bez obilaska u poslednjih 6 meseci',
  'fiveS.board.trend.aria': 'Ocena po mesecima — {points}',
  'fiveS.board.evaluate': 'Oceni',
  'fiveS.board.evaluateMachine': 'Oceni mašinu {name}',
  'fiveS.board.empty.description':
    'Mašine dodaje administrator u Administraciji, pod Radni centri. Ovde se pojave čim postoje.',
  'fiveS.board.empty.mine': 'Nijedna mašina nije dodeljena vama.',
  'fiveS.walk.subtitle': 'Izaberite mašinu, ocenite svaki kriterijum i predajte.',
  'fiveS.picker.title': 'Koju mašinu ocenjujete?',
  'fiveS.picker.due': 'Za obilazak ovaj mesec',
  'fiveS.picker.walked': 'Već obiđene ovaj mesec',
  'fiveS.picker.noTemplate': 'Bez obrasca — ne mogu se oceniti',
  'fiveS.picker.last': 'poslednja ocena',
  'fiveS.scan.title': 'Skenirajte QR nalepnicu mašine',
  'fiveS.scan.hint': 'Usmerite kameru na nalepnicu; mašina se otvara sama.',
  'fiveS.progress': '{answered} / {total} ocenjeno',
  'fiveS.jump.aria': 'Još {count}: idi na prvi neocenjeni kriterijum',
  'fiveS.list.empty.hint': 'Svaki obilazak se pojavi ovde čim se sačuva, najnoviji prvi.',
  'fiveS.list.evaluator': 'Ocenjivač',
}
